import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import AuthShell from "./AuthShell";
import { translateAuthError } from "./Login";
import { listPublicProfessionals, redeemInviteCode, requestLink } from "@/lib/db";
import Avatar from "@/components/common/Avatar";
import { Search, Ticket, Users as UsersIcon, Check } from "lucide-react";

export default function SignupPatient() {
  const { signUp, user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 = data, 2 = link to professional
  const [form, setForm] = useState({ full_name: "", email: "", password: "", accept: false });
  const [mode, setMode] = useState("invite"); // invite | search
  const [code, setCode] = useState("");
  const [pros, setPros] = useState([]);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (step === 2 && mode === "search") {
      const t = setTimeout(() => listPublicProfessionals(q).then(setPros).catch(() => setPros([])), 200);
      return () => clearTimeout(t);
    }
  }, [step, mode, q]);

  if (user) return <Navigate to="/portal" replace />;

  const submitData = (e) => {
    e.preventDefault();
    setErr("");
    if (!form.accept) return setErr("Você precisa aceitar os Termos e a Política de Privacidade.");
    if (form.password.length < 6) return setErr("A senha deve ter ao menos 6 caracteres.");
    setStep(2);
  };

  const finish = async () => {
    setErr("");
    if (mode === "invite" && !code.trim()) return setErr("Informe o código de convite.");
    if (mode === "search" && !picked) return setErr("Selecione um profissional.");
    setBusy(true);
    try {
      // 1) signup
      const { user: u, session } = await signUp({
        email: form.email,
        password: form.password,
        full_name: form.full_name,
        role: "patient",
      });
      // wait briefly for the trigger
      await new Promise(r => setTimeout(r, 700));

      // If e-mail confirmation is on, no session yet — we ask user to confirm and link later in /portal
      if (!session) {
        setSuccess({ kind: "confirm", message: "Sua conta foi criada. Confirme o e-mail e ao entrar você poderá vincular ao profissional." });
        return;
      }

      // 2) link
      if (mode === "invite") {
        await redeemInviteCode(code.trim().toUpperCase());
        setSuccess({ kind: "active", message: "Conta criada e vínculo ativo!" });
      } else {
        await requestLink(picked.id);
        setSuccess({ kind: "pending", message: "Solicitação enviada. Aguarde a aprovação do profissional." });
      }
    } catch (e2) {
      setErr(translateAuthError(e2));
    } finally { setBusy(false); }
  };

  if (success) {
    return (
      <AuthShell title="Pronto!" subtitle={success.message}>
        <div className="ds-card p-5 flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-[hsl(var(--success-bg))] text-[hsl(var(--success-text))] flex items-center justify-center"><Check className="w-5 h-5" /></div>
          <div className="text-sm text-[hsl(var(--text-secondary))]">
            {success.kind === "confirm"
              ? "Verifique sua caixa de entrada para confirmar o e-mail."
              : success.kind === "active"
              ? "Você já pode acessar seu portal."
              : "Você verá o status assim que o profissional aprovar."}
          </div>
        </div>
        <div className="mt-5 flex gap-2">
          <button onClick={() => navigate("/portal")} className="ds-btn-primary w-full h-11">Ir para o portal</button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={step === 1 ? "Criar conta · Paciente" : "Vincular ao profissional"}
      subtitle={step === 1 ? "Em menos de 1 minuto, sem cartão." : "Use um código de convite ou escolha um profissional disponível."}
      footer={step === 1 ? <div className="text-sm text-[hsl(var(--text-secondary))]">Já tem conta? <Link to="/login" className="ds-link">Entrar</Link></div> : null}
    >
      {step === 1 ? (
        <form onSubmit={submitData} className="space-y-4">
          <Field label="Nome completo">
            <input data-testid="pat-name" required className="ds-input" value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} />
          </Field>
          <Field label="E-mail">
            <input data-testid="pat-email" type="email" required className="ds-input" value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
          </Field>
          <Field label="Senha (mín. 6 caracteres)">
            <input data-testid="pat-password" type="password" required minLength={6} className="ds-input" value={form.password} onChange={e => setForm({...form, password: e.target.value})} />
          </Field>
          <label className="flex items-start gap-2 text-sm text-[hsl(var(--text-secondary))]">
            <input data-testid="pat-accept" type="checkbox" className="mt-1" checked={form.accept} onChange={e => setForm({...form, accept: e.target.checked})} />
            <span>Li e aceito os <Link to="/legal/termos" className="ds-link">Termos</Link> e a <Link to="/legal/privacidade" className="ds-link">Política de Privacidade</Link>. Concordo com o tratamento de dados sensíveis de saúde para fins clínicos.</span>
          </label>
          {err && <div className="text-sm text-[hsl(var(--danger-text))] bg-[hsl(var(--danger-bg))] rounded-md px-3 py-2">{err}</div>}
          <button data-testid="pat-next" className="ds-btn-primary w-full h-11">Continuar</button>
        </form>
      ) : (
        <div className="space-y-4">
          <div className="flex p-1 rounded-lg bg-[hsl(var(--bg-base))] border border-[hsl(var(--border-default))]">
            <ModeBtn active={mode === "invite"} onClick={() => setMode("invite")} icon={Ticket} label="Tenho código" testid="mode-invite" />
            <ModeBtn active={mode === "search"} onClick={() => setMode("search")} icon={UsersIcon} label="Buscar profissional" testid="mode-search" />
          </div>

          {mode === "invite" ? (
            <Field label="Código de convite">
              <input data-testid="pat-code" className="ds-input uppercase tracking-wider"
                     placeholder="ABC-1234" value={code} onChange={e => setCode(e.target.value.toUpperCase())} />
            </Field>
          ) : (
            <div>
              <div className="relative mb-3">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-tertiary))]" />
                <input data-testid="pat-search-pro" className="ds-input pl-9" placeholder="Buscar profissional pelo nome..."
                       value={q} onChange={e => setQ(e.target.value)} />
              </div>
              <div className="max-h-72 overflow-y-auto space-y-2">
                {pros.length === 0 && <div className="text-xs text-[hsl(var(--text-tertiary))] py-4 text-center">Nenhum profissional encontrado.</div>}
                {pros.map(p => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPicked(p)}
                    data-testid={`pick-pro-${p.id}`}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${
                      picked?.id === p.id
                        ? "border-[hsl(var(--brand))] bg-[hsl(var(--brand-light))]/40"
                        : "border-[hsl(var(--border-default))] hover:bg-[hsl(var(--bg-hover))]"
                    }`}
                  >
                    <Avatar name={p.full_name} size={36} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{p.full_name}</div>
                      <div className="text-xs text-[hsl(var(--text-tertiary))] truncate">{p.crp ? `CRP ${p.crp}` : "Psicólogo(a)"} {(p.specialties || []).slice(0,2).join(" · ")}</div>
                    </div>
                    {picked?.id === p.id && <Check className="w-4 h-4 text-[hsl(var(--brand))]" />}
                  </button>
                ))}
              </div>
            </div>
          )}

          {err && <div className="text-sm text-[hsl(var(--danger-text))] bg-[hsl(var(--danger-bg))] rounded-md px-3 py-2">{err}</div>}
          <div className="flex gap-2">
            <button type="button" onClick={() => setStep(1)} className="ds-btn-secondary">Voltar</button>
            <button data-testid="pat-finish" disabled={busy} onClick={finish} className="ds-btn-primary flex-1 h-11">
              {busy ? "Criando conta..." : mode === "invite" ? "Vincular e criar conta" : "Solicitar e criar conta"}
            </button>
          </div>
        </div>
      )}
    </AuthShell>
  );
}

function ModeBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick}
            className={`flex-1 inline-flex items-center justify-center gap-2 h-9 rounded-md text-sm font-medium transition-colors ${
              active ? "bg-[hsl(var(--brand))] text-white" : "text-[hsl(var(--text-secondary))] hover:bg-[hsl(var(--bg-hover))]"
            }`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-[hsl(var(--text-secondary))] mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
